import { useCallback, useEffect, useState } from "react";

/**
 * Warns before an in-app browser-back navigation, so a player can't
 * accidentally leave an active game with one misplaced back-press.
 *
 * react-router's useBlocker (the purpose-built primitive for this) needs a
 * data router (createBrowserRouter); this app still uses the plain
 * <BrowserRouter>/<Routes> JSX config, so this hook falls back to a manual
 * popstate interceptor instead (ERD-32): push a buffer history entry, and on
 * the next back-press, immediately push forward again to neutralize the
 * actual navigation before showing a confirmation prompt.
 */
const useBackButtonWarning = (active: boolean) => {
  const [isPromptOpen, setIsPromptOpen] = useState(false);

  useEffect(() => {
    if (!active) return;

    window.history.pushState(null, "", window.location.href);

    const handlePopState = () => {
      window.history.pushState(null, "", window.location.href);
      setIsPromptOpen(true);
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [active]);

  const closePrompt = useCallback(() => setIsPromptOpen(false), []);

  return { isPromptOpen, closePrompt };
};

export default useBackButtonWarning;
